export interface DiffToken {
  type: "equal" | "insert" | "delete";
  text: string;
}

function tokenize(text: string): string[] {
  return text.split(/(\s+)/);
}

function lcs(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  return dp;
}

function backtrack(dp: number[][], a: string[], b: string[], i: number, j: number, out: DiffToken[]) {
  if (i === 0 && j === 0) return;
  if (i === 0) {
    backtrack(dp, a, b, i, j - 1, out);
    out.push({ type: "insert", text: b[j - 1] });
  } else if (j === 0) {
    backtrack(dp, a, b, i - 1, j, out);
    out.push({ type: "delete", text: a[i - 1] });
  } else if (a[i - 1] === b[j - 1]) {
    backtrack(dp, a, b, i - 1, j - 1, out);
    out.push({ type: "equal", text: a[i - 1] });
  } else if (dp[i - 1][j] >= dp[i][j - 1]) {
    backtrack(dp, a, b, i - 1, j, out);
    out.push({ type: "delete", text: a[i - 1] });
  } else {
    backtrack(dp, a, b, i, j - 1, out);
    out.push({ type: "insert", text: b[j - 1] });
  }
}

export function computeDiff(original: string, modified: string): DiffToken[] {
  const a = tokenize(original);
  const b = tokenize(modified);

  if (a.length > 2000 || b.length > 2000) {
    return [
      { type: "delete", text: original },
      { type: "insert", text: modified },
    ];
  }

  const dp = lcs(a, b);
  const out: DiffToken[] = [];
  backtrack(dp, a, b, a.length, b.length, out);
  return out;
}

export function mergeAdjacentTokens(tokens: DiffToken[]): DiffToken[] {
  if (tokens.length === 0) return [];
  const result: DiffToken[] = [{ ...tokens[0] }];
  for (let i = 1; i < tokens.length; i++) {
    const last = result[result.length - 1];
    if (last.type === tokens[i].type) {
      last.text += tokens[i].text;
    } else {
      result.push({ ...tokens[i] });
    }
  }
  return result;
}
